import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useTrips from '../hooks/useTrips';
import '../styles/trips.scss';
import { FiSearch } from 'react-icons/fi';
import 'leaflet/dist/leaflet.css';
import TripMap from '../components/TripMap';
import TripJourneyTimeline from '../components/TripJourneyTimeline';
import { REQUESTER_FILTERS, TRIP_STATE_LABELS, isShopRequest } from '../config/shopTow';

const stateLabels = {
    pending: 'قيد الانتظار',
    driver_on_the_way: 'إلى نقطة البداية',
    trip_started: 'بدأت الرحلة',
    completed: 'مكتملة',
    cancelled: 'ملغية',
    faild: 'فشل'
};

const actorLabels = {
    user: 'المستخدم',
    shopOwner: 'صاحب الورشة',
    driver: 'السائق',
    system: 'النظام',
    admin: 'الإدارة'
};

const ACTIVE_STATES = ['pending', 'driver_on_the_way', 'trip_started'];

// Mirrors the backend rules: an in-progress trip can move to any other state, but a finished trip
// can only be switched to the other finished state (never revived into an active one).
const getAllowedTargetStates = (state) => {
    if (ACTIVE_STATES.includes(state)) {
        return [...ACTIVE_STATES.filter((s) => s !== state), 'completed', 'cancelled'];
    }
    if (state === 'completed') return ['cancelled'];
    if (state === 'cancelled') return ['completed'];
    return ['completed', 'cancelled'];
};

const describeStateChange = (from, to) => {
    if (to === 'completed') {
        return 'سيتم احتساب الرحلة كمكتملة: +1 رحلة للسائق والمستخدم، واحتساب رحلة من اشتراك السائق (أو خصم رسوم الدفع لكل رحلة)، وتطبيق كوبون المستخدم إن وجد.';
    }
    if (to === 'cancelled' && from === 'completed') {
        return 'سيتم التراجع عن احتساب الرحلة: -1 رحلة للسائق والمستخدم، وإرجاع الرحلة إلى اشتراك السائق (أو استرجاع رسوم الرحلة)، وإلغاء استخدام الكوبون.';
    }
    if (to === 'cancelled') {
        return 'سيتم إلغاء الرحلة فوراً لدى السائق والمستخدم وإرسال إشعار لهما بالإلغاء (السبب يظهر للإدارة فقط).';
    }
    return 'سيتم تحديث حالة الرحلة لدى السائق والمستخدم فوراً.';
};

const describeDelete = (state) => {
    if (ACTIVE_STATES.includes(state)) {
        return 'الرحلة ما زالت جارية: سيتم إلغاؤها لدى السائق والمستخدم ثم حذفها نهائياً.';
    }
    if (state === 'completed') {
        return 'سيتم حذف الرحلة نهائياً والتراجع عن احتسابها (-1 رحلة للسائق والمستخدم، وإرجاع استخدام الاشتراك أو رسوم الرحلة، وإلغاء استخدام الكوبون).';
    }
    if (state === 'cancelled') {
        return 'سيتم حذف الرحلة نهائياً والتراجع عن احتساب الإلغاء على السائق.';
    }
    return 'سيتم حذف الرحلة نهائياً.';
};

export const Trips = () => {
    const user = useSelector(state => state.userController.user);
    const navigate = useNavigate();
    const { trips, loading, error, tripCount, totalCost, getTrips, getAvailableMonths, availableMonths, updateTripState, deleteTrip } = useTrips();
    const [searchParams] = useSearchParams();

    // Filter states
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
    const [driverPhone, setDriverPhone] = useState(searchParams.get('driverPhone') || '');
    const [userPhone, setUserPhone] = useState('');
    const [stateFilter, setStateFilter] = useState('');
    const [requesterFilter, setRequesterFilter] = useState('');
    const [showTripModal, setShowTripModal] = useState(false);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [targetState, setTargetState] = useState('');
    const [changeReason, setChangeReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const reasonRef = useRef(null);
    // Journey log of the open trip, oldest first, and the action selected on the timeline/map.
    const [selectedEventIndex, setSelectedEventIndex] = useState(null);
    const journeyEvents = useMemo(
        () => [...(selectedTrip?.journeyLog || [])].sort((a, b) => new Date(a.at) - new Date(b.at)),
        [selectedTrip]
    );

    // Check authentication
    useEffect(() => {
        if (!user || !user.token) {
            navigate('/login');
            return;
        }
    }, [user, navigate]);

    // Load available months and current month trips on initial render
    useEffect(() => {
        if (user && user.token) {
            const initializeData = async () => {
                const months = await getAvailableMonths();
                if (months && months.length > 0) {
                    setSelectedMonth(months[0]); // Select most recent month
                    getTrips({ month: months[0], driverPhone: searchParams.get('driverPhone') || undefined });
                }
            };
            initializeData();
        }
    }, [user]);

    const handleSearch = () => {
        getTrips({
            month: selectedMonth,
            driverPhone: driverPhone || undefined,
            userPhone: userPhone || undefined,
            state: stateFilter || undefined,
            requesterType: requesterFilter || undefined
        });
    };

    const handleTripClick = (trip) => {
        setSelectedTrip(trip);
        setSelectedEventIndex(null);
        setTargetState('');
        setChangeReason('');
        setShowTripModal(true);
    };

    const closeTripModal = () => {
        if (actionLoading) return;
        setShowTripModal(false);
    };

    const startCancelTrip = () => {
        setTargetState('cancelled');
        setTimeout(() => reasonRef.current?.focus(), 0);
    };

    const handleApplyStateChange = async () => {
        if (!selectedTrip || !targetState) return;
        const reason = changeReason.trim();
        if (targetState === 'cancelled' && !reason) {
            reasonRef.current?.focus();
            return;
        }
        const confirmMessage = `تغيير حالة الرحلة #${selectedTrip.tripNumber} من "${stateLabels[selectedTrip.state] || selectedTrip.state}" إلى "${stateLabels[targetState]}"؟\n\n${describeStateChange(selectedTrip.state, targetState)}`;
        if (!window.confirm(confirmMessage)) return;

        setActionLoading(true);
        const updated = await updateTripState(selectedTrip._id, targetState, reason);
        setActionLoading(false);
        if (updated) {
            // Keep the grouped trips of a shop request (the update response doesn't carry them).
            setSelectedTrip(selectedTrip.requestTrips ? { ...updated, requestTrips: selectedTrip.requestTrips } : updated);
            setTargetState('');
            setChangeReason('');
            handleSearch();
        }
    };

    const handleDeleteTrip = async () => {
        if (!selectedTrip) return;
        const confirmMessage = `حذف الرحلة #${selectedTrip.tripNumber}؟ لا يمكن التراجع عن هذا الإجراء.\n\n${describeDelete(selectedTrip.state)}`;
        if (!window.confirm(confirmMessage)) return;

        setActionLoading(true);
        const deleted = await deleteTrip(selectedTrip._id);
        setActionLoading(false);
        if (deleted) {
            setShowTripModal(false);
            setSelectedTrip(null);
            handleSearch();
        }
    };

    if (!user || !user.token) return null;

    return (
        <section className='trips-page'>
            <div className="filters">
                <div className="inputs-container">
                    <select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        className="month-filter"
                        disabled={!availableMonths.length}
                    >
                        {availableMonths.length > 0 ? (
                            availableMonths.map(month => {
                                const date = new Date(month);
                                const label = date.toLocaleDateString('ar-LY', { month: 'long', year: 'numeric' });
                                return (
                                    <option key={month} value={month}>
                                        {label}
                                    </option>
                                );
                            })
                        ) : (
                            <option value="">لا توجد رحلات</option>
                        )}
                    </select>
                    <input
                        type="text"
                        placeholder="رقم هاتف السائق"
                        value={driverPhone}
                        onChange={(e) => setDriverPhone(e.target.value)}
                        className="phone-filter"
                    />
                    <input
                        type="text"
                        placeholder="رقم هاتف المستخدم"
                        value={userPhone}
                        onChange={(e) => setUserPhone(e.target.value)}
                        className="phone-filter"
                    />
                    <select
                        value={stateFilter}
                        onChange={(e) => setStateFilter(e.target.value)}
                        className="state-filter"
                    >
                        <option value="">كل الحالات</option>
                        <option value="completed">مكتملة</option>
                        <option value="cancelled">ملغية</option>
                        <option value="ongoing">جارية</option>
                    </select>
                    <select
                        value={requesterFilter}
                        onChange={(e) => setRequesterFilter(e.target.value)}
                        className="state-filter"
                    >
                        {REQUESTER_FILTERS.map((f) => (
                            <option key={f.value || 'all'} value={f.value}>{f.value ? f.label : 'كل الطالبين'}</option>
                        ))}
                    </select>
                </div>
                <button
                    onClick={handleSearch}
                    className="search-btn"
                    disabled={loading || !availableMonths.length}
                >
                    <FiSearch className="search-icon" />
                    بحث
                </button>
            </div>

            <div className="stats">
                <div className="stat-box right">
                    <span className="label">عدد الرحلات:</span>
                    <span className="value">{tripCount}</span>
                </div>
                <div className="stat-box left">
                    <span className="label">التكلفة الإجمالية:</span>
                    <span className="value">{totalCost.toFixed(2)} د.ل</span>
                </div>
            </div>

            {loading ? (
                <div className="loading">جاري التحميل...</div>
            ) : error ? (
                <div className="error">{error}</div>
            ) : (
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>رقم الرحلة</th>
                                <th>السائق</th>
                                <th>المستخدم</th>
                                <th>اليوم والوقت</th>
                                <th>الشهر</th>
                                <th>السعر</th>
                                <th>الحالة</th>
                                <th>الإجراءات</th>
                            </tr>
                        </thead>
                        <tbody>
                            {trips.map(trip => (
                                <tr key={trip._id}>
                                    <td>{trip.tripNumber}</td>
                                    <td>{trip.driverName}</td>
                                    <td>{trip.userName} {isShopRequest(trip) ? <span className="shop-badge">ورشة</span> : null}</td>
                                    <td>
                                        {new Date(trip.createdAt).toLocaleDateString('ar-LY')} - {' '}
                                        {new Date(trip.createdAt).toLocaleTimeString('ar-LY')}
                                    </td>
                                    <td>
                                        {new Date(trip.createdAt).toLocaleDateString('ar-LY', { month: 'long' })}
                                    </td>
                                    <td>{trip.tripCost.toFixed(2)} د.ل</td>
                                    <td>
                                        <span className={`status ${trip.state}`}>
                                            {stateLabels[trip.state] || '—'}
                                        </span>
                                    </td>
                                    <td>
                                        <button
                                            className="view-trip-btn"
                                            onClick={() => handleTripClick(trip)}
                                        >
                                            عرض الرحلة
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showTripModal && selectedTrip && (
                <div className="modal-overlay" onClick={closeTripModal}>
                    <div className="trip-modal" onClick={e => e.stopPropagation()}>
                        <h2>تفاصيل الرحلة</h2>
                        <div className="trip-state-row">
                            <span className={`status ${selectedTrip.state}`}>
                                {stateLabels[selectedTrip.state] || '—'}
                            </span>
                        </div>
                        <div className="map-container">
                            <TripMap
                                key={selectedTrip._id}
                                trip={selectedTrip}
                                events={journeyEvents}
                                selectedIndex={selectedEventIndex}
                                onSelectEvent={setSelectedEventIndex}
                            />
                        </div>
                        <TripJourneyTimeline
                            events={journeyEvents}
                            selectedIndex={selectedEventIndex}
                            onSelect={setSelectedEventIndex}
                        />
                        <div className="trip-details">
                            <div className="detail-row">
                                <span className="label">نوع الرحلة:</span>
                                <span className="value">
                                    {isShopRequest(selectedTrip)
                                        ? `طلب ورشة · رحلة ${Math.max(1, (selectedTrip.requestTrips || []).findIndex((t) => String(t._id) === String(selectedTrip._id)) + 1)} من ${(selectedTrip.requestTrips || []).length || 1}`
                                        : (selectedTrip.riderName === selectedTrip.userName && selectedTrip.riderPhoneNumber === selectedTrip.userPhoneNumber)
                                            ? 'حجز لنفسه'
                                            : 'حجز لشخص آخر'}
                                </span>
                            </div>
                            <div className="detail-row">
                                <span className="label">رقم الرحلة:</span>
                                <span className="value">{selectedTrip.tripNumber}</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">تكلفة الرحلة:</span>
                                <span className="value">{selectedTrip.tripCost} د.ل</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">نقطة البداية:</span>
                                <span className="value">{selectedTrip.startPoint.address}</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">نقطة النهاية:</span>
                                <span className="value">{selectedTrip.endingPoint.address}</span>
                            </div>
                            {selectedTrip.notes && (
                                <div className="detail-row">
                                    <span className="label">الملاحظات:</span>
                                    <span className="value">{selectedTrip.notes}</span>
                                </div>
                            )}
                            <div className="detail-row">
                                <span className="label">{isShopRequest(selectedTrip) ? 'صاحب الورشة:' : 'بيانات المستخدم:'}</span>
                                <span className="value">{selectedTrip.userName} - {selectedTrip.userPhoneNumber}</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">بيانات الراكب:</span>
                                <span className="value">{selectedTrip.riderName} - {selectedTrip.riderPhoneNumber}</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">بيانات السائق:</span>
                                <span className="value">{selectedTrip.driverName} - {selectedTrip.driverPhoneNumber}</span>
                            </div>
                            {selectedTrip.endReason && (
                                <div className="detail-row">
                                    <span className="label">سبب الإنهاء:</span>
                                    <span className="value">{selectedTrip.endReason}{selectedTrip.endedBy ? ` (بواسطة: ${actorLabels[selectedTrip.endedBy] || selectedTrip.endedBy})` : ''}</span>
                                </div>
                            )}
                            {selectedTrip.cancelReason && (
                                <div className="detail-row">
                                    <span className="label">سبب الإلغاء:</span>
                                    <span className="value">{selectedTrip.cancelReason}{selectedTrip.cancelledBy ? ` (بواسطة: ${actorLabels[selectedTrip.cancelledBy] || selectedTrip.cancelledBy})` : ''}</span>
                                </div>
                            )}
                        </div>

                        {isShopRequest(selectedTrip) && (selectedTrip.requestTrips || []).length > 1 ? (
                            <div className="group-trips">
                                <h4>رحلات نفس الطلب</h4>
                                {selectedTrip.requestTrips.map((t) => {
                                    const current = String(t._id) === String(selectedTrip._id);
                                    const listed = trips.find((x) => String(x._id) === String(t._id));
                                    return (
                                        <div
                                            key={t._id}
                                            className={`group-trip ${current ? 'current' : ''}`}
                                            style={listed && !current ? { cursor: 'pointer' } : undefined}
                                            onClick={listed && !current ? () => handleTripClick(listed) : undefined}
                                        >
                                            <span className="num">#{t.tripNumber}</span>
                                            <span className="driver">{t.driverName}</span>
                                            <span className={`state ${t.state}`}>{TRIP_STATE_LABELS[t.state] || t.state}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : null}

                        <div className="admin-actions">
                            <div className="admin-actions__header">
                                <h3>إدارة الرحلة</h3>
                                {ACTIVE_STATES.includes(selectedTrip.state) && targetState !== 'cancelled' && (
                                    <button
                                        type="button"
                                        className="cancel-trip-btn"
                                        onClick={startCancelTrip}
                                        disabled={actionLoading}
                                    >
                                        إلغاء الرحلة
                                    </button>
                                )}
                            </div>

                            <label className="field">
                                <span>تغيير الحالة إلى</span>
                                <select
                                    value={targetState}
                                    onChange={(e) => setTargetState(e.target.value)}
                                    disabled={actionLoading}
                                >
                                    <option value="">اختر الحالة الجديدة</option>
                                    {getAllowedTargetStates(selectedTrip.state).map((s) => (
                                        <option key={s} value={s}>{stateLabels[s]}</option>
                                    ))}
                                </select>
                            </label>

                            {targetState && (
                                <>
                                    <p className={`effect-hint ${targetState === 'cancelled' ? 'danger' : ''}`}>
                                        {describeStateChange(selectedTrip.state, targetState)}
                                    </p>
                                    {(targetState === 'cancelled' || targetState === 'completed') && (
                                        <label className="field">
                                            <span>
                                                {targetState === 'cancelled' ? 'سبب الإلغاء (مطلوب، يظهر للإدارة فقط)' : 'ملاحظة الإنهاء (اختياري)'}
                                            </span>
                                            <textarea
                                                ref={reasonRef}
                                                rows={2}
                                                value={changeReason}
                                                onChange={(e) => setChangeReason(e.target.value)}
                                                placeholder={targetState === 'cancelled' ? 'اكتب سبب الإلغاء' : 'مثال: تم التأكد من إتمام الرحلة مع السائق'}
                                                disabled={actionLoading}
                                            />
                                        </label>
                                    )}
                                    <div className="admin-actions__buttons">
                                        <button
                                            type="button"
                                            className={`apply-btn ${targetState === 'cancelled' ? 'danger' : ''}`}
                                            onClick={handleApplyStateChange}
                                            disabled={actionLoading || (targetState === 'cancelled' && !changeReason.trim())}
                                        >
                                            {actionLoading ? 'جاري التنفيذ...' : targetState === 'cancelled' ? 'تأكيد الإلغاء' : 'تطبيق التغيير'}
                                        </button>
                                        <button
                                            type="button"
                                            className="ghost-btn"
                                            onClick={() => { setTargetState(''); setChangeReason(''); }}
                                            disabled={actionLoading}
                                        >
                                            تراجع
                                        </button>
                                    </div>
                                </>
                            )}

                            <button
                                type="button"
                                className="delete-trip-btn"
                                onClick={handleDeleteTrip}
                                disabled={actionLoading}
                            >
                                حذف الرحلة نهائياً
                            </button>
                        </div>

                        <button className="close-btn" onClick={closeTripModal} disabled={actionLoading}>
                            إغلاق
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};